export interface BatchIdResponse {
  batchId: string;
}

export interface BatchDocumentKeyField {
  key: string;
  value: string;
}

export interface BatchDocumentRequest {
  documentId: string;
  source: string;
  status?: string;
  documentOrder?: number;
}

export interface CreateBatchRequest {
  batchId?: string;
  name?: string;
  department: string;
  securityGroup?: string;
  defaultArea?: string;
  defaultDocumentType?: string;
  keyFields: BatchDocumentKeyField[];
  documents?: BatchDocumentRequest[];
  status?: string;
}

export interface BatchStatistics {
  documentCount: number;
  unprocessedDocumentCount: number;
  earliestExpirationDate?: string;
  expiringDocumentCount: number;
  expiredDocumentCount: number;
}

export interface BatchDocumentResponse extends BatchDocumentRequest {
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  version: number;
  batch?: BatchResponse;
}

export interface BatchResponse extends CreateBatchRequest {
  documents?: BatchDocumentResponse[];
  checkedOutAt?: string;
  checkedOutBy?: string;
  completedAt?: string;
  completedBy?: string;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  version: number;
  statistics: BatchStatistics;
}

export interface BatchListResponse {
  items: BatchResponse[];
  count?: number;
}

export interface BulkCreateBatchDocumentRequest {
  documents: BatchDocumentRequest[];
}

export interface BatchDocumentListResponse {
  items: BatchDocumentResponse[];
  count?: number;
  batch?: BatchResponse;
}

export interface ListBatchesParams {
  offset?: number;
  count?: number;
  status?: string;
  minCreatedAt?: Date;
  maxCreatedAt?: Date;
}

export interface UpdateBatchRequest {
  name?: string;
  department?: string;
  securityGroup?: string;
  defaultArea?: string;
  defaultDocumentType?: string;
  keyFields?: BatchDocumentKeyField[];
  status?: string;
  version: number;
}

export interface UpdateBatchDocumentRequest {
  status?: string;
  source?: string;
  version: number;
}

export interface PrivilegeDepartment {
  code: string;
  name: string;
  status?: string[];
}

export interface PrivilegeContext {
  departments: PrivilegeDepartment[];
  status?: string[];
}

export interface Privilege {
  operationName: string;
  context: PrivilegeContext;
}

export class BatchSdk {
  private baseURL: string;

  /**
   * Constructor
   * @param {string} baseURL - The base URL for the service
   */
  constructor(baseURL: string) {
    if (!baseURL || baseURL.trim() === '') {
      throw new Error('baseURL is required and cannot be empty');
    }

    try {
      new URL(baseURL);
    } catch (error) {
      throw new Error('baseURL must be a valid URL');
    }

    this.baseURL = baseURL;
  }

  /**
   * Wrapper for handling fetch requests and error handling
   * @param {string} endpoint - API endpoint
   * @param {RequestInit} [options] - Request options
   * @returns {Promise<Response>} - Response or throws an error
   */
  private async fetchWrapper(endpoint: string, options?: RequestInit): Promise<Response> {
    const url = `${this.baseURL}${endpoint}`;
    const response = await fetch(url, options);

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage += `, message: ${errorData.message}`;
        }
      } catch (e) {
        // ignore
      }
      throw new Error(errorMessage);
    }

    return response;
  }

  /**
   * Create a Batch ID
   * @param {string} accessToken - user's access token
   * @returns {Promise<BatchIdResponse>} - Created Batch ID
   */
  async createBatchId(accessToken: string): Promise<BatchIdResponse> {
    const endpoint = `/batches/batchId`;
    const response = await this.fetchWrapper(endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    return response.json();
  }

  /**
   * Create a new Batch
   * @param {string} accessToken - user's access token
   * @param {CreateBatchRequest} data - Request body for creating a batch
   * @returns {Promise<BatchResponse>} - Created batch details
   */
  async createBatch(accessToken: string, data: CreateBatchRequest): Promise<BatchResponse> {
    const endpoint = `/batches`;
    const response = await this.fetchWrapper(endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    return response.json();
  }

  /**
   * Update an existing Batch
   * @param {string} accessToken - user's access token
   * @param {string} batchId - Batch identifier
   * @param {UpdateBatchRequest} data - Request body for updating a batch
   * @returns {Promise<BatchResponse>} - Updated batch details
   */
  async updateBatch(
    accessToken: string,
    batchId: string,
    data: UpdateBatchRequest
  ): Promise<BatchResponse> {
    const endpoint = `/batches/${batchId}`;
    const response = await this.fetchWrapper(endpoint, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    return response.json();
  }

  /**
   * Get details of a specific Batch
   * @param {string} accessToken - user's access token
   * @param {string} batchId - Batch identifier
   * @param {string} [include] - Optional include query parameter (e.g., 'document')
   * @returns {Promise<BatchResponse>} - Batch details
   */
  async getBatch(accessToken: string, batchId: string, include?: string): Promise<BatchResponse> {
    let endpoint = `/batches/${batchId}`;
    if (include) {
      endpoint += `?include=${encodeURIComponent(include)}`;
    }
    const response = await this.fetchWrapper(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    return response.json();
  }

  /**
   * List Batches with optional filters
   * @param {string} accessToken - user's access token
   * @param {ListBatchesParams} [params] - Optional parameters object for listing batches
   * @returns {Promise<BatchListResponse>} - List of batches
   */
  async listBatches(
    accessToken: string,
    params: ListBatchesParams = {}
  ): Promise<BatchListResponse> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value != null) {
        if (value instanceof Date) {
          queryParams.append(key, value.toISOString());
        } else {
          queryParams.append(key, value.toString());
        }
      }
    });

    const endpoint = `/batches?${queryParams.toString()}`;
    const response = await this.fetchWrapper(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    return response.json();
  }

  /**
   * Delete a Batch
   * @param {string} accessToken - user's access token
   * @param {string} batchId - Batch identifier
   * @returns {Promise<void>}
   */
  async deleteBatch(accessToken: string, batchId: string): Promise<void> {
    const endpoint = `/batches/${batchId}`;
    await this.fetchWrapper(endpoint, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });
  }

  /**
   * Bulk create documents in a batch
   * @param {string} accessToken - user's access token
   * @param {string} batchId - Batch identifier
   * @param {BulkCreateBatchDocumentRequest} data - List of documents to add to the batch
   * @returns {Promise<BatchDocumentListResponse>} - Created batch documents
   */
  async bulkCreateBatchDocuments(
    accessToken: string,
    batchId: string,
    data: BulkCreateBatchDocumentRequest
  ): Promise<BatchDocumentListResponse> {
    const endpoint = `/batches/${batchId}/documents/bulk`;
    const response = await this.fetchWrapper(endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    return response.json();
  }

  /**
   * Get documents in a batch
   * @param {string} accessToken - user's access token
   * @param {string} batchId - Batch identifier
   * @returns {Promise<BatchDocumentListResponse>} - Batch documents
   */
  async getBatchDocuments(
    accessToken: string,
    batchId: string
  ): Promise<BatchDocumentListResponse> {
    const endpoint = `/batches/${batchId}/documents`;
    const response = await this.fetchWrapper(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    return response.json();
  }

  /**
   * Get documents in a batch
   * @param {string} accessToken - user's access token
   * @param {string} batchId - Batch identifier
   * @param {string} documentId - document identifier
   * @returns {Promise<BatchDocumentResponse>} - Batch documents
   */
  async getBatchDocument(
    accessToken: string,
    batchId: string,
    documentId: string
  ): Promise<BatchDocumentResponse> {
    const endpoint = `/batches/${batchId}/documents/${documentId}`;
    const response = await this.fetchWrapper(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    return response.json();
  }

  /**
   * Update a specific document in a batch
   * @param {string} accessToken - user's access token
   * @param {string} batchId - Batch identifier
   * @param {string} documentId - Document identifier
   * @param {UpdateBatchDocumentRequest} data - Request body for updating a batch document
   * @returns {Promise<BatchDocumentResponse>} - Updated document details
   */
  async updateBatchDocument(
    accessToken: string,
    batchId: string,
    documentId: string,
    data: UpdateBatchDocumentRequest
  ): Promise<BatchDocumentResponse> {
    const endpoint = `/batches/${batchId}/documents/${documentId}`;
    const response = await this.fetchWrapper(endpoint, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    return response.json();
  }

  /**
   * Remove a document from a batch
   * @param {string} accessToken - user's access token
   * @param {string} batchId - Batch identifier
   * @param {string} documentId - Document identifier
   * @returns {Promise<BatchResponse>} - Updated batch details
   */
  async removeBatchDocument(
    accessToken: string,
    batchId: string,
    documentId: string
  ): Promise<BatchResponse> {
    const endpoint = `/batches/${batchId}/documents/${documentId}`;
    const response = await this.fetchWrapper(endpoint, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    return response.json();
  }

  /**
   * Checkout a Batch
   * @param {string} accessToken - user's access token
   * @param {string} batchId - Batch identifier
   * @returns {Promise<BatchResponse>} - Updated batch details
   */
  async checkoutBatch(accessToken: string, batchId: string): Promise<BatchResponse> {
    const endpoint = `/batches/${batchId}/checkout`;
    const response = await this.fetchWrapper(endpoint, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    return response.json();
  }

  /**
   * Checkin a Batch
   * @param {string} accessToken - user's access token
   * @param {string} batchId - Batch identifier
   * @returns {Promise<BatchResponse>} - Updated batch details
   */
  async checkinBatch(accessToken: string, batchId: string): Promise<BatchResponse> {
    const endpoint = `/batches/${batchId}/checkin`;
    const response = await this.fetchWrapper(endpoint, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });
    return response.json();
  }

  /**
   * Get Batch Privileges
   * @param {string} accessToken - user's access token
   * @returns {Promise<UserPrivilegesResponse>} - User privileges
   */
  async getBatchPrivileges(accessToken: string): Promise<Privilege[]> {
    const endpoint = `/batches/privileges`;
    const response = await this.fetchWrapper(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });
    return response.json();
  }
}
